import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { ListChecks, PackageOpen, Search } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { profileClientApi, productsApi, type CouponItem, type CouponItemInput } from "@/lib/api";
import { cn, codeSeries, isExamProgrammedOrderItem, mapEtapaToGradeName, mapEtapaToSegment } from "@/lib/utils";

interface CouponItemPickerProps {
  clientCode: string;
  cycleYear: number;
  value: CouponItemInput[];
  onChange: (items: CouponItemInput[]) => void;
  /** Itens já gravados no cupom (edição/detalhes): usados para exibir a descrição se o perfil não carregar. */
  savedItems?: CouponItem[];
  readOnly?: boolean;
}

interface PickerRow {
  productId: number;
  productSeries: string | null;
  description: string;
  /** Código do produto (CXC). */
  code: string;
  sku: string;
  /** Tipo do produto, pela descrição (Aluno, Professor…), e não pelo id. */
  usage: string;
  contractualQuantity: number;
}

const normalize = (text: string) =>
  text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

/** Busca por descrição, código CXC ou SKU, sem diferenciar acento nem maiúscula. */
const matchesRow = (row: PickerRow, query: string) => {
  const term = normalize(query);
  if (!term) return true;
  return [row.description, row.code, row.sku].some((value) => !!value && normalize(value).includes(term));
};

interface Group {
  key: string;
  segment: string | null;
  items: PickerRow[];
}

/**
 * Agrupa os produtos por segmento (Educação Infantil, Fundamental I…) e, dentro dele, por série, só neste fluxo do
 * cupom. Segmentos e séries seguem a ordem escolar; "Outros" fica no fim. O seletor de itens da troca/checklist
 * continua agrupando por série (groupItemsBySeries, sem alteração).
 */
const groupRowsBySegment = (rows: PickerRow[]): Group[] => {
  const groups = new Map<string, Group>();
  for (const row of rows) {
    const key = mapEtapaToSegment(row.productSeries);
    const group = groups.get(key);
    if (group) group.items.push(row);
    else groups.set(key, { key, segment: null, items: [row] });
  }
  const order = (group: Group) => Math.min(...group.items.map((row) => codeSeries(mapEtapaToGradeName(row.productSeries))));
  for (const group of groups.values()) {
    group.items.sort(
      (a, b) =>
        codeSeries(mapEtapaToGradeName(a.productSeries)) - codeSeries(mapEtapaToGradeName(b.productSeries))
        || a.description.localeCompare(b.description, "pt-BR"),
    );
  }
  return Array.from(groups.values()).sort((a, b) => order(a) - order(b));
};

interface SeriesBlock {
  key: string;
  name: string;
  items: PickerRow[];
}

const seriesKey = (segment: string, series: string) => `${segment}::${series}`;

/** Séries dentro de um segmento, na ordem escolar (os itens do segmento já vêm ordenados por série). */
const seriesOf = (group: Group): SeriesBlock[] => {
  const blocks = new Map<string, SeriesBlock>();
  for (const row of group.items) {
    const name = mapEtapaToGradeName(row.productSeries);
    const block = blocks.get(name);
    if (block) block.items.push(row);
    else blocks.set(name, { key: seriesKey(group.key, name), name, items: [row] });
  }
  return Array.from(blocks.values());
};

/** Chaves (segmentos e séries) dos grupos visíveis, para abrir tudo o que a busca/filtro encontrou. */
const allKeysOf = (groups: Group[]): string[] =>
  groups.flatMap((group) => [group.key, ...seriesOf(group).map((block) => block.key)]);

const filterGroups = (groups: Group[], query: string, onlyIds: number[] | null): Group[] => {
  if (!query.trim() && !onlyIds) return groups;
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((row) => matchesRow(row, query) && (!onlyIds || onlyIds.includes(row.productId))),
    }))
    .filter((group) => group.items.length > 0);
};

/**
 * Seleção dos itens bonificados do cupom, no mesmo padrão do seletor de itens da solicitação de divergência:
 * produtos do perfil do cliente no ciclo (rotas já existentes de perfil e produtos), agrupados por série, com busca
 * e "Só selecionados". Produtos de 2º semestre aparecem (o desconto fica reservado e é abatido na cobrança do pedido
 * programado); avaliações/simulados programados não aparecem. A quantidade bonificada vai de 1 até a quantidade
 * contratual.
 */
export function CouponItemPicker({ clientCode, cycleYear, value, onChange, savedItems = [], readOnly }: CouponItemPickerProps) {
  const profileQuery = useQuery({
    queryKey: ["coupon-item-picker", "profile", clientCode, cycleYear],
    queryFn: () => profileClientApi.getByClientCode(clientCode, cycleYear),
    staleTime: 60_000,
  });
  const productsQuery = useQuery({
    queryKey: ["coupon-item-picker", "products", cycleYear],
    queryFn: () => productsApi.getAll(cycleYear),
    staleTime: 60_000,
  });

  const [query, setQuery] = useState("");
  const [openGroups, setOpenGroups] = useState<string[] | null>(null);
  // "Só selecionados": os marcados quando o filtro foi ligado (desmarcar não tira a linha de baixo do cursor).
  const [pinnedIds, setPinnedIds] = useState<number[] | null>(null);

  const rows = useMemo<PickerRow[]>(() => {
    const products = productsQuery.data?.data ?? [];
    const byProduct = new Map<number, PickerRow>();
    for (const entry of (profileQuery.data?.data ?? []) as any[]) {
      const productId = Number(entry.productId);
      const product = products.find((p) => p.id === productId);
      if (!productId || !product) continue;
      // Avaliação/simulado programado fica de fora; 2º semestre pode ser selecionado.
      if (isExamProgrammedOrderItem({ scheduling_category: entry.schedulingCategory })) continue;
      // A API de produtos devolve a descrição em camelCase (descricaoProduto), como o pedido já lê.
      const description: string = (product as any).descricaoProduto ?? product.descricao_produto ?? "";
      const contractual = Number(entry.contractualQuantity) || 0;
      const existing = byProduct.get(productId);
      if (existing) {
        existing.contractualQuantity += contractual;
        continue;
      }
      byProduct.set(productId, {
        productId,
        productSeries: product.serie ?? null,
        description,
        code: product.codigoProduto || product.codigo_produto || "",
        sku: product.sku,
        usage: (product.utilizacao ?? "").trim(),
        contractualQuantity: contractual,
      });
    }
    return Array.from(byProduct.values()).filter((row) => row.contractualQuantity > 0);
  }, [profileQuery.data, productsQuery.data, cycleYear]);

  const groups = useMemo(() => groupRowsBySegment(rows), [rows]);
  const selected = useMemo(() => new Map(value.map((item) => [item.productId, item.quantity])), [value]);
  const selectedIds = value.map((item) => item.productId);
  const visibleGroups = filterGroups(groups, query, pinnedIds);

  // Abertos ao montar: os segmentos (todos até 3, ou o primeiro mais os que têm item marcado) e as séries com item marcado.
  const expandedGroups = openGroups ?? [
    ...groups
      .filter((group, index) => groups.length <= 3 || index === 0 || group.items.some((row) => selected.has(row.productId)))
      .map((group) => group.key),
    ...groups.flatMap((group) =>
      seriesOf(group).filter((block) => block.items.some((row) => selected.has(row.productId))).map((block) => block.key)),
  ];
  const segmentKeys = new Set(groups.map((group) => group.key));
  const openSegments = expandedGroups.filter((key) => segmentKeys.has(key));
  // Cada nível troca só as suas chaves e preserva as do outro.
  const updateLevel = (levelKeys: Set<string>) => (next: string[]) =>
    setOpenGroups([...expandedGroups.filter((key) => !levelKeys.has(key)), ...next]);

  // Troca de cliente/ciclo: descarta itens que não existem no novo perfil (evita enviar produto fora do perfil).
  useEffect(() => {
    if (readOnly || profileQuery.isLoading || productsQuery.isLoading || profileQuery.isError || productsQuery.isError) return;
    const valid = value.filter((item) => rows.some((row) => row.productId === item.productId));
    if (valid.length !== value.length) onChange(valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, readOnly]);

  const toggle = (row: PickerRow, checked: boolean) => {
    if (checked) {
      onChange([...value.filter((item) => item.productId !== row.productId), { productId: row.productId, quantity: 1 }]);
    } else {
      onChange(value.filter((item) => item.productId !== row.productId));
    }
  };

  const setQuantity = (row: PickerRow, raw: string) => {
    const parsed = Math.floor(Number(raw));
    const quantity = Number.isFinite(parsed) ? Math.min(Math.max(parsed, 0), row.contractualQuantity) : 0;
    onChange(value.map((item) => (item.productId === row.productId ? { ...item, quantity } : item)));
  };

  const handleSearchChange = (text: string) => {
    setQuery(text);
    if (text.trim()) setOpenGroups(allKeysOf(filterGroups(groups, text, pinnedIds)));
  };

  const handleToggleOnlySelected = () => {
    if (pinnedIds) {
      setPinnedIds(null);
      return;
    }
    setPinnedIds(selectedIds);
    setOpenGroups(allKeysOf(filterGroups(groups, query, selectedIds)));
  };

  // Enter na busca não pode enviar o formulário do cupom.
  const preventSubmit = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") event.preventDefault();
  };

  if (profileQuery.isLoading || productsQuery.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (profileQuery.isError || productsQuery.isError) {
    if (readOnly && savedItems.length > 0) return <SavedItemsList items={savedItems} />;
    return <p className="text-sm text-destructive">Não foi possível carregar os produtos do cliente neste ciclo.</p>;
  }

  if (rows.length === 0) {
    if (readOnly && savedItems.length > 0) return <SavedItemsList items={savedItems} />;
    return (
      <div className="flex flex-col items-center gap-2 rounded-[10px] border border-dashed px-6 py-8 text-center">
        <PackageOpen className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">Este cliente não possui produtos elegíveis no ciclo {cycleYear}.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            value={query}
            onChange={(event) => handleSearchChange(event.target.value)}
            onKeyDown={preventSubmit}
            placeholder="Buscar por descrição, código ou SKU"
            aria-label="Buscar produto por descrição, código ou SKU"
            autoComplete="off"
            className="h-10 pl-9 text-[14.5px]"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleToggleOnlySelected}
          disabled={!pinnedIds && selectedIds.length === 0}
          aria-pressed={!!pinnedIds}
          className={cn(
            "h-10 shrink-0 gap-2 text-[14px]",
            pinnedIds && "border-primary bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary",
          )}
        >
          <ListChecks className="h-4 w-4" aria-hidden="true" />
          Só selecionados ({selectedIds.length})
        </Button>
      </div>

      {visibleGroups.length === 0 ? (
        <p className="rounded-[10px] border border-dashed px-6 py-6 text-center text-sm text-muted-foreground">
          {query.trim() ? `Nenhum produto encontrado para "${query.trim()}".` : "Nenhum produto selecionado."}
        </p>
      ) : (
        <Accordion type="multiple" value={openSegments} onValueChange={updateLevel(segmentKeys)} className="flex flex-col gap-2">
          {visibleGroups.map((group) => {
            const selectedCount = group.items.filter((row) => selected.has(row.productId)).length;
            return (
              <AccordionItem key={group.key} value={group.key} className="rounded-[10px] border bg-background">
                <AccordionTrigger className="gap-3 px-4 py-3 text-left hover:no-underline">
                  <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
                    <span className="text-[15px] font-bold">{group.key}</span>
                    {group.segment && <span className="text-[13px] text-muted-foreground">{group.segment}</span>}
                  </span>
                  <span
                    className={
                      selectedCount > 0
                        ? "shrink-0 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary"
                        : "shrink-0 text-xs text-muted-foreground"
                    }
                  >
                    {selectedCount > 0
                      ? `${selectedCount} ${selectedCount === 1 ? "selecionado" : "selecionados"}`
                      : `${group.items.length} ${group.items.length === 1 ? "produto" : "produtos"}`}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  {(() => {
                    const blocks = seriesOf(group);
                    const blockKeys = new Set(blocks.map((block) => block.key));
                    return (
                      <Accordion
                        type="multiple"
                        value={expandedGroups.filter((key) => blockKeys.has(key))}
                        onValueChange={updateLevel(blockKeys)}
                        className="flex flex-col gap-2"
                      >
                        {blocks.map((block) => {
                          const blockSelected = block.items.filter((row) => selected.has(row.productId)).length;
                          return (
                            <AccordionItem key={block.key} value={block.key} className="rounded-lg border bg-muted/20">
                              <AccordionTrigger className="gap-3 px-3 py-2.5 text-left hover:no-underline">
                                <span className="min-w-0 flex-1 text-[14px] font-semibold">{block.name}</span>
                                <span
                                  className={
                                    blockSelected > 0
                                      ? "shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary"
                                      : "shrink-0 text-xs text-muted-foreground"
                                  }
                                >
                                  {blockSelected > 0
                                    ? `${blockSelected} ${blockSelected === 1 ? "selecionado" : "selecionados"}`
                                    : `${block.items.length} ${block.items.length === 1 ? "produto" : "produtos"}`}
                                </span>
                              </AccordionTrigger>
                              <AccordionContent className="flex flex-col gap-2.5 px-2.5 pb-2.5">
                                {block.items.map((row) => (
                                  <CouponItemCard
                                    key={row.productId}
                                    row={row}
                                    quantity={selected.get(row.productId)}
                                    readOnly={readOnly}
                                    onToggle={(checked) => toggle(row, checked)}
                                    onQuantityChange={(raw) => setQuantity(row, raw)}
                                  />
                                ))}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    );
                  })()}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </div>
  );
}

interface CouponItemCardProps {
  row: PickerRow;
  /** Quantidade a bonificar; undefined = produto não selecionado. */
  quantity: number | undefined;
  readOnly?: boolean;
  onToggle: (checked: boolean) => void;
  onQuantityChange: (raw: string) => void;
}

function CouponItemCard({ row, quantity, readOnly, onToggle, onQuantityChange }: CouponItemCardProps) {
  const selected = quantity !== undefined;
  const controlId = `coupon-item-${row.productId}`;
  const quantityId = `coupon-item-quantity-${row.productId}`;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3.5 gap-y-1 rounded-[10px] border transition-colors",
        selected ? "border-2 border-primary bg-primary/5" : "hover:bg-muted/40",
        readOnly && "opacity-80",
      )}
    >
      <label
        htmlFor={controlId}
        className={cn(
          "flex min-w-0 flex-1 basis-[240px] items-center gap-3.5 py-3.5 pl-4",
          selected && "py-[13px] pl-[15px]",
          readOnly ? "cursor-default" : "cursor-pointer",
        )}
      >
        <Checkbox
          id={controlId}
          checked={selected}
          disabled={readOnly}
          onCheckedChange={(checked) => onToggle(checked === true)}
          className="h-5 w-5 rounded-[5px] border-[1.5px] border-input bg-background data-[state=checked]:border-primary [&_svg]:h-3.5 [&_svg]:w-3.5 [&_svg]:stroke-[3]"
        />
        <span className="flex min-w-0 flex-col gap-1">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-[15px] font-semibold leading-snug">{row.description || "Produto sem descrição"}</span>
            <span className="text-[13px] text-muted-foreground">Contratual: {row.contractualQuantity}</span>
          </span>
          <span className="text-xs text-muted-foreground">
            SKU {row.sku || "—"} · {row.usage || "—"}
          </span>
        </span>
      </label>

      {selected ? (
        <div className="flex shrink-0 items-center gap-2.5 pb-3 pl-[51px] pr-[15px] sm:pb-0 sm:pl-0">
          <label htmlFor={quantityId} className="text-[13px] text-muted-foreground">
            Bonificar
            <span className="sr-only"> {row.description} (máximo {row.contractualQuantity})</span>
          </label>
          <span className="flex h-9 items-center rounded-lg border bg-background px-1 focus-within:ring-2 focus-within:ring-ring">
            <input
              id={quantityId}
              type="number"
              inputMode="numeric"
              min={1}
              max={row.contractualQuantity}
              step={1}
              value={quantity || ""}
              disabled={readOnly}
              onChange={(event) => onQuantityChange(event.target.value)}
              aria-required
              className="w-14 bg-transparent text-center text-[15px] font-bold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          </span>
          <span className="text-[13px] text-muted-foreground">de {row.contractualQuantity}</span>
        </div>
      ) : (
        <span className="shrink-0 pr-4 text-[13px] text-muted-foreground">Qtd. {row.contractualQuantity}</span>
      )}
    </div>
  );
}

function SavedItemsList({ items }: { items: CouponItem[] }) {
  return (
    <ul className="space-y-1 rounded-md border p-3 text-sm">
      {items.map((item) => (
        <li key={item.productId} className="flex justify-between gap-2">
          <span>{item.productDescription ?? item.productCode ?? item.productId}</span>
          <span className="font-medium">{item.quantity}</span>
        </li>
      ))}
    </ul>
  );
}
